/**
 * Profile validation utilities for rental applications
 */

// Required fields for rental applications
export const REQUIRED_FIELDS = [
  'firstName',
  'lastName', 
  'email',
  'phoneNumber',
  'moveInDate',
  'occupation',
  'monthlyIncome',
  'pets',
  'smoking',
  'creditScore',
  'numAdults',
  'numKids',
  'termDuration'
];

/**
 * Validate user profile and return missing fields
 * @param {Object} userProfile - User profile data
 * @returns {Array} Array of missing field names
 */
export const validateProfileForRental = (userProfile) => {
  const missingFields = [];

  if (!userProfile) {
    return REQUIRED_FIELDS; // All fields are missing
  }

  REQUIRED_FIELDS.forEach(field => {
    const value = userProfile[field];
    
    // Check if field is missing or empty
    if (value === null || value === undefined || value === '' || 
        (typeof value === 'string' && value.trim() === '')) {
      missingFields.push(field);
    }
  });

  return missingFields;
};

/**
 * Get user profile data from various sources (session, API response, etc.)
 * @param {Object} sessionUserInfo - User info from session manager
 * @param {Object} apiProfileData - Profile data from API
 * @returns {Object} Consolidated profile data
 */
export const consolidateProfileData = (sessionUserInfo, apiProfileData) => {
  const consolidated = {};

  // Start with session data
  if (sessionUserInfo) {
    consolidated.phoneNumber = sessionUserInfo.phoneNumber;
  }

  // Override/add with API profile data
  if (apiProfileData) {
    // Basic profile fields
    consolidated.firstName = apiProfileData.first_name || apiProfileData.firstName;
    consolidated.lastName = apiProfileData.last_name || apiProfileData.lastName;
    consolidated.email = apiProfileData.email;
    consolidated.phoneNumber = apiProfileData.phone_number || consolidated.phoneNumber;

    // Extended profile fields from tenant_profile
    consolidated.moveInDate = apiProfileData.target_move_in_date || apiProfileData.moveInDate;
    consolidated.occupation = apiProfileData.occupation;
    consolidated.monthlyIncome = apiProfileData.monthly_income || apiProfileData.monthlyIncome;
    
    // Handle pets field - check if it's boolean or string
    if (apiProfileData.pet !== undefined && apiProfileData.pet !== null) {
      consolidated.pets = apiProfileData.pet ? 'yes' : 'no';
    } else if (apiProfileData.pets) {
      consolidated.pets = apiProfileData.pets;
    } else {
      consolidated.pets = '';
    }
    
    // Handle smoking field - check if it's boolean or string  
    if (apiProfileData.smoking !== undefined && apiProfileData.smoking !== null) {
      consolidated.smoking = apiProfileData.smoking ? 'yes' : 'no';
    } else {
      consolidated.smoking = '';
    }
    
    consolidated.creditScore = apiProfileData.credit_score || apiProfileData.creditScore;
    consolidated.numAdults = apiProfileData.num_adults || apiProfileData.numAdults || '1';
    consolidated.numKids = apiProfileData.num_kids || apiProfileData.numKids || '0';
    consolidated.termDuration = apiProfileData.duration_of_stay || apiProfileData.durationOfStay || apiProfileData.termDuration || '';
  }

  return consolidated;
};

/**
 * Format profile data for API submission
 * @param {Object} profileData - Profile data from form
 * @returns {Object} Formatted data for API
 */
export const formatProfileForAPI = (profileData) => {
  return {
    // Basic profile
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    email: profileData.email,
    phone_number: profileData.phoneNumber,

    // Extended profile (tenant_profile table format)
    target_move_in_date: profileData.moveInDate,
    occupation: profileData.occupation,
    monthly_income: parseFloat(profileData.monthlyIncome) || 0,
    pet: profileData.pets === 'yes',
    smoking: profileData.smoking === 'yes',
    credit_score: profileData.creditScore,
    num_adults: parseInt(profileData.numAdults) || 1,
    num_kids: parseInt(profileData.numKids) || 0,
    duration_of_stay: profileData.termDuration
  };
};

/**
 * Get readable field names for display
 * @param {Array} fieldNames - Array of field names
 * @returns {Array} Array of readable field names
 */
export const getReadableFieldNames = (fieldNames) => {
  const fieldLabels = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    moveInDate: 'Move-in Date',
    occupation: 'Occupation',
    monthlyIncome: 'Monthly Income',
    pets: 'Pet Information',
    smoking: 'Smoking Status',
    creditScore: 'Credit Score',
    numAdults: 'Number of Adults',
    numKids: 'Number of Children'
  };

  return fieldNames.map(field => fieldLabels[field] || field);
};
